import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Button,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Card,
  CardContent,
  CardActions,
  Chip,
  IconButton,
  Tooltip,
  Pagination,
  Badge,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  DialogActions,
  Dialog,
  DialogTitle,
  DialogContent,
  CircularProgress,
} from "@mui/material";
import {
  Add as AddIcon,
  DirectionsBus as BusIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  DirectionsBus,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useFirebaseAuth } from "../../hooks/useFirebaseAuth";
import { useToast } from "../../components/Toast";
import { Loading } from "../../components/Loading";
import { getValidToken } from "../../utils/authUtils";
import {
  commonButtonProps,
  commonTextFieldProps,
  smallButtonProp,
} from "../../utils/styles";
import { usePlanStatus } from "../../hooks/usePlanStatus";

interface Vehicle {
  id: string;
  status: string;
  vehicleType: string;
  acquisitionType: string;
  vehicleNumber: string;
  chassisNumber: string;
  makeYear: number;
  model: string;
  color: string;
  bodyType: string;
  configuration: string;
  fuelType: string;
  seatingCapacity: number;
  isAC: boolean;
  nextMaintenanceDate: string;
  rcNumber: string;
  rcExpiryDate: string;
  pucNumber: string;
  pucEndDate: string;
  outsourcedOwnerId?: string;
}

interface OutsourcedOwner {
  id: string;
  companyName: string;
  primaryContactName: string;
  primaryContactNumber: string;
  gstPercentage: number;
}

interface OutsourcedVehicle extends OutsourcedOwner {
  vehicles: Vehicle[];
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function VehicleTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 1 }}>{children}</Box>}
    </div>
  );
}

function vehicleA11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
    sx: {
      fontSize: "0.75rem",
      px: 0.5,
      py: 0.5,
    },
  };
}

export const updateOutsourcedOwner = async (owner: OutsourcedOwner) => {
  const token = await getValidToken();
  const response = await axios.put(
    `${process.env.REACT_APP_API_HOST}vehicles/outsourced-owner/${owner.id}`,
    owner,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
  return response.data;
};

const Vehicle: React.FC = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { user, loading: authLoading } = useFirebaseAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [filteredVehicles, setFilteredVehicles] = useState<Vehicle[]>([]);
  const [outsourcedVehicles, setOutsourcedVehicles] = useState<
    OutsourcedVehicle[]
  >([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({
    status: "",
    bodyType: "",
    isAC: "",
    search: "",
    acquisitionType: "",
  });
  const [search, setSearch] = useState("");
  const { planStatus } = usePlanStatus();
  const [value, setValue] = React.useState(0);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  useEffect(() => {
    if (!user || authLoading) return;
    fetchVehicles(value);
  }, [
    user,
    authLoading,
    page,
    filters.status,
    filters.bodyType,
    filters.isAC,
    filters.search,
    filters.acquisitionType,
    value,
  ]);

  const fetchVehicles = async (tabIndex: number) => {
    if (!user) return;

    setIsLoading(true);
    try {
      const token = await getValidToken();
      const queryParams = new URLSearchParams({
        acquisitionType: tabIndex === 0 ? "Owned" : "Outsourced",
        ...(tabIndex === 1 && { forVehicles: "true" }),
        ...(tabIndex === 0 && { page: page.toString() }),
        ...(tabIndex === 0 && { limit: "10" }),
        ...(filters.status && { status: filters.status }),
        ...(filters.bodyType && { bodyType: filters.bodyType }),
        ...(filters.isAC && { isAC: filters.isAC }),
        ...(filters.search && { search: filters.search }),
      });

      const url = `${process.env.REACT_APP_API_HOST}vehicles/v2/get?${queryParams}`;
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data) {
        if (tabIndex === 1) {
          // For outsourced vehicles, handle the grouped structure
          setOutsourcedVehicles(response.data.data || []);
          setVehicles([]);
          setFilteredVehicles([]);
        } else {
          // For owned vehicles, handle as before
          setVehicles(response.data.data || []);
          setFilteredVehicles(response.data.data || []);
        }
        setTotalPages(response.data.pagination?.pages || 1);
      }
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilterChange = (field: string, value: string) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setPage(1); // Reset to first page when filters change
  };

  const handleSearch = (value: string) => {
    setFilters((prev) => ({ ...prev, search: value }));
    setPage(1);
  };

  const clearFilters = () => {
    setFilters({
      status: "",
      bodyType: "",
      isAC: "",
      search: "",
      acquisitionType: "",
    });
    setPage(1);
  };

  const hasActiveFilters = () => {
    return Object.values(filters).some((value) => value !== "");
  };

  if (authLoading || isLoading) {
    return <Loading message="Loading vehicles..." fullScreen={false} />;
  }

  const handleAddNewVehicle = () => {
    if (value === 0) {
      navigate("/vehicles/add");
    } else {
      navigate("/vehicles/add?outsourced=true");
    }
  };

  return (
    <>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="h4" component="h1" sx={{ fontSize: "1.25rem" }}>
          Vehicle Fleet
        </Typography>
        {planStatus?.permissions?.canCreateVehicle && (
          <Button
            variant="contained"
            startIcon={<AddIcon fontSize="small" />}
            onClick={() => handleAddNewVehicle()}
            {...commonButtonProps}
          >
            {value === 0 ? "Owned Vehicle" : "Outsourced Vehicle"}
          </Button>
        )}
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 1, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Search vehicle number"
              variant="outlined"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleSearch(search);
                }
              }}
              InputProps={{
                endAdornment: (
                  <IconButton onClick={() => handleSearch(search)}>
                    <SearchIcon
                      sx={{ color: "text.secondary" }}
                      fontSize="small"
                    />
                  </IconButton>
                ),
              }}
              {...commonTextFieldProps}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth>
              <InputLabel
                {...commonTextFieldProps}
                sx={{
                  fontSize: "0.75rem",
                }}
              >
                Status
              </InputLabel>
              <Select
                value={filters.status}
                label="Status"
                onChange={(e) => handleFilterChange("status", e.target.value)}
                {...commonTextFieldProps}
                sx={{
                  fontSize: "0.75rem",
                }}
              >
                <MenuItem value="" sx={{ fontSize: "0.75rem" }}>
                  All
                </MenuItem>
                <MenuItem value="AVAILABLE" sx={{ fontSize: "0.75rem" }}>
                  Available
                </MenuItem>
                <MenuItem
                  value="UNDER MAINTENANCE"
                  sx={{ fontSize: "0.75rem" }}
                >
                  Under Maintenance
                </MenuItem>
                <MenuItem value="ON ROUTE" sx={{ fontSize: "0.75rem" }}>
                  On Route
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth>
              <InputLabel
                {...commonTextFieldProps}
                sx={{
                  fontSize: "0.75rem",
                }}
              >
                Body Type
              </InputLabel>
              <Select
                value={filters.bodyType}
                label="Body Type"
                onChange={(e) => handleFilterChange("bodyType", e.target.value)}
                {...commonTextFieldProps}
                sx={{
                  fontSize: "0.75rem",
                }}
              >
                <MenuItem value="" sx={{ fontSize: "0.75rem" }}>
                  All
                </MenuItem>
                <MenuItem value="SLEEPER" sx={{ fontSize: "0.75rem" }}>
                  Sleeper
                </MenuItem>
                <MenuItem value="SEATER" sx={{ fontSize: "0.75rem" }}>
                  Seater
                </MenuItem>
                <MenuItem value="SEMI_SLEEPER" sx={{ fontSize: "0.75rem" }}>
                  Semi Sleeper
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth>
              <InputLabel
                {...commonTextFieldProps}
                sx={{
                  fontSize: "0.75rem",
                }}
              >
                AC Type
              </InputLabel>
              <Select
                value={filters.isAC}
                label="AC Type"
                onChange={(e) => handleFilterChange("isAC", e.target.value)}
                {...commonTextFieldProps}
                sx={{
                  fontSize: "0.75rem",
                }}
              >
                <MenuItem value="" sx={{ fontSize: "0.75rem" }}>
                  All
                </MenuItem>
                <MenuItem value="true" sx={{ fontSize: "0.75rem" }}>
                  AC
                </MenuItem>
                <MenuItem value="false" sx={{ fontSize: "0.75rem" }}>
                  Non-AC
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>
          {hasActiveFilters() && (
            <Grid item xs={12} sm={6} md="auto" sx={{ marginLeft: "auto" }}>
              <Button
                variant="outlined"
                color="error"
                onClick={clearFilters}
                {...commonButtonProps}
              >
                Clear Filters
              </Button>
            </Grid>
          )}
        </Grid>
      </Paper>

      {/* Vehicle Grid */}
      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs value={value} onChange={handleChange} aria-label="vehicle tabs">
          <Tab label="Owned" {...vehicleA11yProps(0)} />
          <Tab label="Outsourced" {...vehicleA11yProps(1)} />
        </Tabs>
      </Box>
      <VehicleTabPanel value={value} index={0}>
        <VehicleCards
          vehicles={vehicles}
          hasActiveFilters={hasActiveFilters}
          navigate={navigate}
          showToast={showToast}
          planStatus={planStatus}
          totalPages={totalPages}
          page={page}
          setPage={setPage}
        />
      </VehicleTabPanel>
      <VehicleTabPanel value={value} index={1}>
        <OutsourcedVehicleAccordions
          outsourcedVehicles={outsourcedVehicles}
          navigate={navigate}
          showToast={showToast}
          planStatus={planStatus}
          onSave={() => {
            fetchVehicles(1);
          }}
        />
        {/* Pagination for outsourced vehicles */}
        {totalPages > 1 && (
          <Box display="flex" justifyContent="center" mt={4}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_, value) => setPage(value)}
              color="primary"
            />
          </Box>
        )}
      </VehicleTabPanel>
    </>
  );
};

const VehicleCards = ({
  vehicles,
  hasActiveFilters,
  navigate,
  showToast,
  planStatus,
  totalPages,
  page,
  setPage,
}: {
  vehicles: Vehicle[];
  hasActiveFilters: () => boolean;
  navigate: (path: string) => void;
  showToast: (
    message: string,
    type: "default" | "error" | "success" | "warning" | "info"
  ) => void;
  planStatus: any;
  totalPages: number;
  page: number;
  setPage: (page: number) => void;
}) => {
  const getStatusColor = (status: string) => {
    switch (status?.toUpperCase()) {
      case "AVAILABLE":
        return "success";
      case "UNDER MAINTENANCE":
        return "warning";
      case "ON ROUTE":
        return "info";
      default:
        return "default";
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status?.toUpperCase()) {
      case "AVAILABLE":
        return "Available";
      case "UNDER MAINTENANCE":
        return "Under Maintenance";
      case "ON ROUTE":
        return "On Route";
      default:
        return status || "-";
    }
  };

  return (
    <>
      <Grid container spacing={2}>
        {vehicles.length === 0 ? (
          <Grid item xs={12}>
            <Paper sx={{ p: 2, textAlign: "center" }}>
              {!hasActiveFilters() ? (
                <>
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No vehicles available
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    Add a new vehicle to get started
                  </Typography>
                </>
              ) : (
                <>
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No vehicles found with your selected filters
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    Try changing or removing the filters to see more vehicle
                    options
                  </Typography>
                </>
              )}
            </Paper>
          </Grid>
        ) : (
          vehicles.map((vehicle) => (
            <Grid item xs={12} sm={4} md={2.4} key={vehicle?.id}>
              <Card
                sx={{
                  height: "100%",
                  cursor: "pointer",
                  "&:hover": { boxShadow: 6 },
                }}
                onClick={() => {
                  if (planStatus?.permissions?.canViewVehicle) {
                    navigate(`/vehicles/${vehicle?.id}`);
                  } else {
                    showToast(
                      "You don't have permission to view this vehicle",
                      "warning"
                    );
                  }
                }}
              >
                <CardContent>
                  <Box display="flex" alignItems="center">
                    <DirectionsBus
                      sx={{ fontSize: 24, color: "primary.main", mr: 1 }}
                    />
                    <Box>
                      <Typography
                        variant="h6"
                        component="div"
                        fontSize="0.75rem"
                      >
                        {vehicle?.vehicleNumber || "-"}
                      </Typography>
                    </Box>
                  </Box>

                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    my={1}
                  >
                    <Chip
                      label={getStatusLabel(vehicle?.status)}
                      color={getStatusColor(vehicle?.status)}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: "0.6rem" }}
                    />
                    <Chip
                      label={vehicle?.acquisitionType}
                      // color={vehicle?.isAC ? "primary" : "default"}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: "0.6rem" }}
                    />
                  </Box>

                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        fontSize="0.7rem"
                      >
                        Seats: {vehicle?.seatingCapacity}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        fontSize="0.7rem"
                      >
                        Fuel: {vehicle?.fuelType}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          ))
        )}
      </Grid>
      {/* Pagination */}
      {totalPages > 1 && (
        <Box display="flex" justifyContent="center" mt={4}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, value) => setPage(value)}
            color="primary"
          />
        </Box>
      )}
    </>
  );
};

const OutsourcedVehicleAccordions = ({
  outsourcedVehicles,
  navigate,
  showToast,
  planStatus,
  onSave,
}: {
  outsourcedVehicles: OutsourcedVehicle[];
  navigate: (path: string) => void;
  showToast: (
    message: string,
    type: "default" | "error" | "success" | "warning" | "info"
  ) => void;
  planStatus: any;
  onSave: () => void;
}) => {
  const [expanded, setExpanded] = React.useState<string | false>(false);
  const [editOwnerModalOpen, setEditOwnerModalOpen] = useState(false);
  const [editOwner, setEditOwner] = useState<OutsourcedOwner>({
    id: "",
    companyName: "",
    primaryContactName: "",
    primaryContactNumber: "",
    gstPercentage: 0,
  });
  const handleChange =
    (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : false);
    };

  const getStatusColor = (status: string) => {
    switch (status?.toUpperCase()) {
      case "AVAILABLE":
        return "success";
      case "UNDER MAINTENANCE":
        return "warning";
      case "ON ROUTE":
        return "info";
      default:
        return "default";
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status?.toUpperCase()) {
      case "AVAILABLE":
        return "Available";
      case "UNDER MAINTENANCE":
        return "Under Maintenance";
      case "ON ROUTE":
        return "On Route";
      default:
        return status || "-";
    }
  };

  if (outsourcedVehicles.length === 0) {
    return (
      <Paper sx={{ p: 2, textAlign: "center" }}>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No outsourced vehicles found
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Add outsourced vehicles to see them here
        </Typography>
      </Paper>
    );
  }

  return (
    <Box>
      {outsourcedVehicles?.map((owner, index) => (
        <Accordion
          key={owner.id}
          expanded={expanded === `panel${index}`}
          onChange={handleChange(`panel${index}`)}
          sx={{ mb: 1 }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls={`panel${index}bh-content`}
            id={`panel${index}bh-header`}
          >
            <Grid container spacing={1} mr={3}>
              <Grid item xs={12} md={10}>
                <Box display="flex" alignItems="center">
                  <DirectionsBus
                    sx={{ fontSize: 24, color: "primary.main", mr: 2 }}
                  />
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" sx={{ fontSize: "0.8rem" }}>
                      {owner.companyName}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} md={1}>
                <Button
                  variant="contained"
                  color="primary"
                  {...smallButtonProp}
                  onClick={(e) => {
                    e.stopPropagation();
                    setEditOwnerModalOpen(true);
                    setEditOwner(owner);
                  }}
                >
                  Edit Owner
                </Button>
              </Grid>
              <Grid item xs={12} md={1}>
                <Chip
                  label={`${owner.vehicles.length} Vehicle${
                    owner.vehicles.length !== 1 ? "s" : ""
                  }`}
                  color="primary"
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: "0.65rem", borderRadius: 1 }}
                />
              </Grid>
            </Grid>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              {owner.vehicles.map((vehicle) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={vehicle.id}>
                  <Card
                    sx={{
                      height: "100%",
                      cursor: "pointer",
                      "&:hover": { boxShadow: 6 },
                    }}
                    onClick={() => {
                      if (planStatus?.permissions?.canViewVehicle) {
                        navigate(`/vehicles/${vehicle.id}`);
                      } else {
                        showToast(
                          "You don't have permission to view this vehicle",
                          "warning"
                        );
                      }
                    }}
                  >
                    <CardContent>
                      <Box display="flex" alignItems="center">
                        <DirectionsBus
                          sx={{ fontSize: 20, color: "primary.main", mr: 1 }}
                        />
                        <Box>
                          <Typography
                            variant="h6"
                            component="div"
                            fontSize="0.75rem"
                          >
                            {vehicle.vehicleNumber || "-"}
                          </Typography>
                        </Box>
                      </Box>

                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        my={1}
                      >
                        <Chip
                          label={getStatusLabel(vehicle.status)}
                          color={getStatusColor(vehicle.status)}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: "0.6rem" }}
                        />
                      </Box>

                      <Grid container spacing={1}>
                        <Grid item xs={6}>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            fontSize="0.65rem"
                          >
                            Seats: {vehicle.seatingCapacity || 0}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            fontSize="0.65rem"
                          >
                            Fuel: {vehicle.fuelType || "-"}
                          </Typography>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </AccordionDetails>
        </Accordion>
      ))}
      <EditOwnerModal
        open={editOwnerModalOpen}
        onClose={() => setEditOwnerModalOpen(false)}
        ownerData={editOwner}
        onSave={() => {
          setEditOwnerModalOpen(false);
          onSave(); // This will trigger fetchVehicles(1) from the parent component
        }}
      />
    </Box>
  );
};

export default Vehicle;

const EditOwnerModal = ({
  open,
  onClose,
  ownerData,
  onSave,
}: {
  open: boolean;
  onClose: () => void;
  ownerData: OutsourcedOwner;
  onSave: () => void;
}) => {
  const [owner, setOwner] = useState<OutsourcedOwner>(ownerData);
  const [isLoading, setIsLoading] = useState(false);
  const { showToast } = useToast();
  useEffect(() => {
    setOwner(ownerData);
  }, [ownerData]);

  const handleSave = async () => {
    setIsLoading(true);
    try {
      const res = await updateOutsourcedOwner(owner);
      if (res.isSuccess) {
        showToast("Owner updated successfully", "success");
        onSave();
      } else {
        showToast();
      }
    } catch (error) {
      showToast();
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Edit Owner</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} mt={1}>
          <Grid item xs={12} md={6}>
            <TextField
              label="Company Name"
              {...commonTextFieldProps}
              fullWidth
              value={owner.companyName}
              onChange={(e) =>
                setOwner({ ...owner, companyName: e.target.value })
              }
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Primary Contact Name"
              {...commonTextFieldProps}
              fullWidth
              value={owner.primaryContactName}
              onChange={(e) =>
                setOwner({ ...owner, primaryContactName: e.target.value })
              }
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Primary Contact Number"
              {...commonTextFieldProps}
              fullWidth
              value={owner.primaryContactNumber}
              onChange={(e) =>
                setOwner({ ...owner, primaryContactNumber: e.target.value })
              }
              type="number"
              inputProps={{
                inputMode: "numeric",
                pattern: "[0-9]*",
                maxLength: 10,
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="GST Percentage"
              {...commonTextFieldProps}
              fullWidth
              value={owner.gstPercentage}
              type="number"
              onChange={(e) =>
                setOwner({
                  ...owner,
                  gstPercentage: Number(e.target.value),
                })
              }
              inputProps={{
                min: 0,
                max: 100,
              }}
              InputProps={{
                endAdornment: (
                  <span style={{ marginRight: 8, fontSize: "0.7rem" }}>%</span>
                ),
              }}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClose}
          {...commonButtonProps}
          variant="outlined"
          color="error"
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          {...commonButtonProps}
          variant="contained"
          color="primary"
          disabled={isLoading}
        >
          {isLoading ? <CircularProgress size={20} /> : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
